import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * featured-blog — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Blog posts" / featured-blog section
 * (shrine-pro-ref/sections/featured-blog.liquid + assets/section-featured-blog.css + component-article-card.css).
 * A heading over a grid of ARTICLE cards (cover image → title → date/author → excerpt) that flips to a
 * horizontal peek slider on mobile — with the faithful Shrine "Blog post" placeholder when no articles exist.
 *
 * Config parity: EVERY Shrine setting is mapped 1:1 (Shopify → ours): inline_richtext→text (with bold /
 * italic highlight markup), the two heading-highlight engines + heading size, `blog` post-count knobs
 * (post_limit + columns_desktop), the show_image/date/author/view_all toggles, the 5 Shrine color schemes,
 * device visibility, section id, and top/bottom padding (mobile = ×0.75, exactly as Shrine). The `blog`
 * PICKER is intentionally omitted for now — aquilixShop has no blog/article backend yet, so (like collage /
 * collection-list / featured-collection) the section demo-populates and the picker lands with the data layer.
 *
 * No background-PATTERN engine and no `custom` color scheme here — Shrine's featured-blog schema ships
 * neither, and this is an exact-config rebuild.
 */
const GENERAL = 'General';
const BLOG = 'Blog';
export const featuredBlogSchema = {
    type: 'featured-blog',
    name: 'Blog posts',
    version: 1,
    templates: ['index', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading + highlight + size ─────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: GENERAL,
            default: 'Blog posts',
            defaultKey: 'section.blog_posts',
            info: 'Wrap words in ** ** for highlight 1, * * for highlight 2.',
        },
        ...headingHighlightFields(1, GENERAL),
        ...headingHighlightFields(2, GENERAL),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: GENERAL,
            default: 'background-1',
            info: 'Applies to the section background; the cards use the theme card colors.',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Blog: post count + columns + card toggles ───────────────────────────────
        {
            id: 'postLimit',
            type: 'range',
            label: 'Number of blog posts to show',
            group: BLOG,
            min: 2,
            max: 4,
            step: 1,
            default: 3,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Number of columns on desktop',
            group: BLOG,
            min: 1,
            max: 4,
            step: 1,
            default: 3,
        },
        {
            id: 'showImage',
            type: 'checkbox',
            label: 'Show featured image',
            group: BLOG,
            default: true,
            info: 'For best results, use an image with a 3:2 aspect ratio.',
        },
        {
            id: 'showDate',
            type: 'checkbox',
            label: 'Show date',
            group: BLOG,
            default: true,
        },
        {
            id: 'showAuthor',
            type: 'checkbox',
            label: 'Show author',
            group: BLOG,
            default: false,
        },
        {
            id: 'showViewAll',
            type: 'checkbox',
            label: 'Enable "View all" button if the blog has more blog posts than shown',
            group: BLOG,
            default: true,
        },
        mediaRadiusField,
        // ── Section padding ─────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
};
export const featuredBlogPresets = [
    {
        name: 'Blog posts',
        settings: {
            headingSize: 'h1',
            postLimit: 3,
            columnsDesktop: 3,
            showImage: true,
            showDate: true,
            showAuthor: false,
            showViewAll: true,
            colorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const featuredBlogMeta = {
    label: 'Blog posts',
    group: 'Home',
    icon: 'file-text',
};
//# sourceMappingURL=schema.js.map